<template>
    <div class="section">
        <div class="center-section">
            <div class="form-heading">
                <h1 class="header-1">{{ $t('auth.login.heading') }}</h1>
                <img src="@/assets/wavehand.png" :alt="$t('app.logoAlt')" />
            </div>
            <div class="signup-form">
                <div v-if="errorMessage" class="error-message">
                    {{ errorMessage }}
                </div>
                <form @submit.prevent="handleSubmit">
                    <base-input class="form-group" type="email" v-model="email" :placeholder="$t('auth.email')" required
                        icon="envelop">
                    </base-input>
                    <base-input class="form-group" type="password" v-model="password" :placeholder="$t('auth.password')" required
                        icon="lock">
                    </base-input>
                    <primary-btn :loading="isLoading" :disabled="isLoading">{{ $t('auth.login.signIn') }}</primary-btn>
                    <router-link to="/resetpass" class="reset link caption">{{ $t('auth.login.resetPassword') }}</router-link>
                </form>
                <div class="or-divider">
                    <span class="or-divider-line"></span>
                    <span class="or-divider-text caption">{{ $t('auth.or') }}</span>
                    <span class="or-divider-line"></span>
                </div>
                <GoogleSignIn />
            </div>
            <div class="login-link caption">
                {{ $t('auth.login.noAccount') }} <router-link to="/signup" class="link">{{ $t('auth.login.createAccount') }}</router-link>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import BaseInput from '@/components/BaseComponents/BaseInput.vue';
import PrimaryBtn from '@/components/BaseComponents/PrimaryBtn.vue';
import GoogleSignIn from '@/components/AuthProviders/GoogleSignIn.vue';
import { trackEvent, trackFormSubmission, trackError } from '@/utils/analytics';
import { useI18n } from 'vue-i18n';

const email = ref('');
const password = ref('');
const localErrorMessage = ref('');
const isLoading = ref(false);

const router = useRouter();
const store = useStore();
const { t } = useI18n();


const errorMessage = computed(() => localErrorMessage.value || store.getters.getRedirectMessage);

async function handleSubmit() {
    localErrorMessage.value = '';
    store.dispatch('setRedirectMessage', null);

    trackEvent('login_started', {
        email_domain: email.value.split('@')[1] || 'unknown'
    });

    const url = `${process.env.VUE_APP_BACKEND_URL}/login`;
    const requestBody = {
        email: email.value,
        password: password.value,
    };

    isLoading.value = true;

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            if (response.status === 401) {
                localErrorMessage.value = t('auth.login.invalidCredentials');
            } else if (response.status === 403) {
                localErrorMessage.value = t('auth.login.verifyEmail');
            } else if (response.status === 429) {
                localErrorMessage.value = t('auth.login.tooManyAttempts');
            } else if (response.status >= 500) {
                localErrorMessage.value = t('auth.login.serverError');
            } else {
                localErrorMessage.value = t('auth.login.failed');
            }
            trackEvent('login_failed', {
                email_domain: email.value.split('@')[1] || 'unknown'
            });
            trackFormSubmission('login_form', false);
            trackError('login_error', localErrorMessage.value, 'login_form');
            return;
        }

        const result = await response.json();
        const sessionId = result.session_id;
        const userFolderPath = result.user_folder_path;

        store.dispatch('login', { sessionId, userFolderPath, email: email.value });
        trackEvent('login_success', {
            email_domain: email.value.split('@')[1] || 'unknown'
        });
        trackFormSubmission('login_form', true);

        router.push('/app');
    } catch (error) {
        console.error('Error during Login:', error);
        localErrorMessage.value = t('auth.login.connectionError');
        trackEvent('login_failed', {
            email_domain: email.value.split('@')[1] || 'unknown'
        });
        trackFormSubmission('login_form', false);
        trackError('login_error', error.message || 'Unknown error', 'login_form');
    } finally {
        isLoading.value = false;
    }
}
</script>

<style scoped>
.center-section {
    width: 30%;
    margin: auto;
}

.form-heading {
    display: flex;
    justify-content: center;
    padding-bottom: var(--spacing-xl);
}

.form-heading img {
    max-width: 100%;
    height: auto;
    object-fit: contain;
}

.signup-form {
    padding: var(--spacing-2xl);
    margin-bottom: var(--spacing-lg);
    background-color: var(--bg-card);
    border-radius: var(--border-radius-2xl);
    box-shadow: var(--shadow-md);
}

.signup-form form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
}

.reset {
    margin-top: var(--spacing-xl);
    display: flex;
    justify-content: center;
}

.login-link {
    text-align: center;
    color: var(--color-white-40);
}

.link {
    color: var(--color-white);
    text-decoration: none;
}

.error-message {
    color: var(--color-error);
    text-align: center;
    margin-bottom: var(--spacing-xl);
}

.or-divider {
    display: flex;
    align-items: center;
    margin: var(--spacing-xl) 0;
}

.or-divider-line {
    flex: 1;
    height: 1px;
    background-color: var(--color-white-10);
}

.or-divider-text {
    padding: 0 var(--spacing-md);
    color: var(--color-white-40);
}

@media (max-width: 1000px) {
    .center-section {
        width: 80%;
    }

    .signup-form {
        padding: var(--spacing-xl);
    }

    .form-group {
        padding: var(--spacing-md);
    }
}

@media (max-width: 480px) {
    .form-heading {
        padding-bottom: var(--spacing-md);
    }

    .form-heading .header-1 {
        font-size: var(--font-size-h2);
    }

    .signup-form {
        padding: var(--spacing-lg);
    }

    .or-divider {
        margin: var(--spacing-md) 0;
    }

    .reset {
        margin-top: var(--spacing-md);
    }
}
</style>
